// Б.3: Сквилл active — «Выбери юнита врага, он атакует случайного своего
// союзного юнита».
//
// Цель: вражеский юнит на поле (НЕ Скрытный).
//
// Предусловие: у врага должно быть минимум 2 юнита на поле — выбранная цель
// и хотя бы один союзник для случайной атаки. Иначе активка недоступна.
//
// Эффект:
//   1. Среди оставшихся вражеских юнитов (всех, кроме выбранной цели)
//      выбирается случайная цель для атаки.
//   2. Применяется Провокация: если среди оставшихся есть TAUNT-юнит — выбор
//      ограничивается TAUNT-юнитами. Если несколько TAUNT — случайный.
//   3. Атака идёт через стандартный unitVsUnit: forward + ответный удар,
//      все стандартные триггеры на полученный урон, Посмертия при смерти.
//   4. Атака разово, без FRENZY. Один обмен.
//   5. НЕ влияет на правило «контратаковал — пасует» в Ф3 — это активка вне
//      Ф3, отдельное событие. Флаги counterAttackedThisPhase не трогаем.

import { unitVsUnit } from '../../combat.js';
import { hasKeyword, KW } from '../../keywords.js';
import { onUnitDamaged, onUnitDied } from '../deus/index.js';

export const HERO_ID = 'syn_skvill';

// Цели Сквилла — вражеские НЕ-Стелс юниты.
export function getSkvillActiveTargets(state, playerIdx) {
  const enemy = state.players[1 - playerIdx];
  return enemy.unitsOnField().filter(u => !hasKeyword(u, KW.STEALTH));
}

export function skvillActiveAvailable(state, playerIdx) {
  const p = state.players[playerIdx];
  if (!p || p.hero.id !== HERO_ID) return false;
  if (p.heroActiveUsed) return false;
  if (p.hero.hp <= 0) return false;
  const enemy = state.players[1 - playerIdx];
  // ≥2 юнитов на стороне врага (1 — выбранная цель, 2 — для атаки).
  if (enemy.unitsOnField().length < 2) return false;
  if (getSkvillActiveTargets(state, playerIdx).length === 0) return false;
  return true;
}

// targetCard — вражеский юнит, который АТАКУЕТ. defender выбирается случайно
// среди остальных вражеских юнитов с учётом Провокации.
export function skvillActive(state, playerIdx, attackerCard) {
  if (!skvillActiveAvailable(state, playerIdx)) {
    return { ok: false, reason: 'unavailable' };
  }
  const enemy = state.players[1 - playerIdx];
  if (!attackerCard || attackerCard.type !== 'unit' || !attackerCard.isAlive()) {
    return { ok: false, reason: 'invalid_target' };
  }
  if (!enemy.unitsOnField().includes(attackerCard)) {
    return { ok: false, reason: 'not_enemy_unit' };
  }
  if (hasKeyword(attackerCard, KW.STEALTH)) {
    return { ok: false, reason: 'stealth' };
  }
  // Кандидаты на цель — все вражеские юниты, кроме атакующего.
  const candidates = enemy.unitsOnField().filter(u => u !== attackerCard);
  if (candidates.length === 0) {
    return { ok: false, reason: 'no_defender' };
  }
  // Провокация: если среди кандидатов есть TAUNT — выбор только из TAUNT.
  const taunts = candidates.filter(u => hasKeyword(u, KW.TAUNT));
  const pool = taunts.length > 0 ? taunts : candidates;
  const defender = pool[state.rng.int(pool.length)];

  const me = state.players[playerIdx];
  state.log.add(
    `✨ Сквилл [${me.name}] [active]: ${attackerCard.name_ru} атакует ${defender.name_ru} ` +
    `[${enemy.name}]${taunts.length > 0 ? ' (Провокация)' : ''}`
  );

  // unitVsUnit: forward урон, контратака. Б.3: не трогаем counterAttackedThisPhase
  // (это отдельное событие вне Ф3).
  const r = unitVsUnit(attackerCard, defender, state.currentTurn);
  state.log.add(
    `⚔ ${attackerCard.name_ru} [${enemy.name}] → ${defender.name_ru} [${enemy.name}]: ${r.atkDmg} урона ` +
    `(HP ${r.defenderBefore.hp}→${r.defenderAfter.hp}, броня ${r.defenderBefore.armor}→${r.defenderAfter.armor})`
  );
  if (r.defDmg > 0) {
    state.log.add(
      `⚔ ${defender.name_ru} [${enemy.name}] → ${attackerCard.name_ru} [${enemy.name}]: ${r.defDmg} урона (ответный) ` +
      `(HP ${r.attackerBefore.hp}→${r.attackerAfter.hp}, броня ${r.attackerBefore.armor}→${r.attackerAfter.armor})`
    );
  }
  // Триггеры на полученный урон.
  const defTaken = (r.fwd.armorAbsorbed || 0) + (r.fwd.hpLost || 0);
  if (defTaken > 0) {
    onUnitDamaged(state, { ownerIdx: enemy.id, card: defender, dmg: defTaken });
  }
  const atkTaken = (r.bwd.armorAbsorbed || 0) + (r.bwd.hpLost || 0);
  if (atkTaken > 0) {
    onUnitDamaged(state, { ownerIdx: enemy.id, card: attackerCard, dmg: atkTaken });
  }
  // Смерти.
  if (defender.current.hp <= 0) {
    enemy.removeFromField(defender);
    state.log.add(`✗ ${defender.name_ru} [${enemy.name}] погибает`);
    onUnitDied(state, {
      ownerIdx: enemy.id, card: defender,
      killer: attackerCard, killerIdx: enemy.id, killerWasCounter: false
    });
  }
  if (attackerCard.current.hp <= 0) {
    enemy.removeFromField(attackerCard);
    state.log.add(`✗ ${attackerCard.name_ru} [${enemy.name}] погибает`);
    onUnitDied(state, {
      ownerIdx: enemy.id, card: attackerCard,
      killer: defender, killerIdx: enemy.id, killerWasCounter: true
    });
  }
  me.heroActiveUsed = true;
  return { ok: true, attacker: attackerCard, defender };
}
